import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Typography,
  ButtonGroup,
  ButtonGroupTypes,
  FormInput,
  Alert,
} from '@ecdlink/ui';
import { useForm, useFormState } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import { EditInfantDetailsProps } from './infant-details.types';
import {
  InfantDetailsModel,
  infantDetailsModelSchema,
} from '@/schemas/infant/infant-details';
import { intervalToDuration, setMonth, setYear, sub } from 'date-fns';
import DatePicker from 'react-datepicker';
import { useSelector } from 'react-redux';
import { staticDataSelectors } from '@store/static-data';
import { getPreviousAndNextMonths } from '@ecdlink/core';
import { getVisitDates } from '@/utils/infant/infantVisit.utils';

export const InfantDetails: React.FC<EditInfantDetailsProps> = ({
  onSubmit,
  numberOfChildren,
  multipleChildrenCount,
  motherInfo,
  name,
  genderId,
  dateOfBirth,
  setInfantVisits,
}) => {
  const {
    getValues: getInfantDetailsFormValues,
    setValue: setInfantDetailsFormValue,
    register: infantFormRegister,
    control: infantDetailsFormControl,
  } = useForm<InfantDetailsModel>({
    resolver: yupResolver(infantDetailsModelSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: name || '',
      genderId: genderId || '',
      dateOfBirth: dateOfBirth || undefined,
    },
  });
  const genders = useSelector(staticDataSelectors.getGenders);

  const currentDate = useMemo(() => new Date(), []);
  const twoYearsAgo = sub(currentDate, { years: 2 });
  const visitTypes = useSelector(staticDataSelectors.getInfantVisitTypes);

  const { expectedDateOfDelivery, eddMinDate, eddMaxDate } = useMemo(() => {
    const expectedDateOfDelivery = motherInfo?.expectedDateOfDelivery
      ? new Date(motherInfo?.expectedDateOfDelivery)
      : null;

    if (expectedDateOfDelivery) {
      const { previousDate: eddMinDate } = getPreviousAndNextMonths(
        expectedDateOfDelivery,
        6
      );
      const { nextDate } = getPreviousAndNextMonths(expectedDateOfDelivery, 3);
      const eddMaxDate = nextDate < currentDate ? nextDate : currentDate;

      return {
        expectedDateOfDelivery,
        eddMinDate,
        eddMaxDate,
      };
    }

    return { expectedDateOfDelivery };
  }, [currentDate, motherInfo?.expectedDateOfDelivery]);

  const genderOptionsUpdated = genders
    ?.filter((gender) => gender?.description !== 'Other')
    .map((item) => {
      return {
        text: item?.description,
        value: String(item?.id),
      };
    });

  // Initialize date state with either the passed dateOfBirth or currentDate
  const initialDate = dateOfBirth || currentDate;
  const [myMonth, setMyMonth] = useState(initialDate);
  const [myYear, setMyYear] = useState(initialDate);
  const [myDay, setMyDay] = useState(initialDate);

  const minDate = new Date(myYear.getFullYear(), myMonth.getMonth(), 1);
  const maxDate = new Date(myYear.getFullYear(), myMonth.getMonth() + 1, 0);
  const { years, months, days } = intervalToDuration({
    start: myDay > new Date() ? new Date() : myDay,
    end: currentDate,
  });
  const { isValid } = useFormState({ control: infantDetailsFormControl });

  useEffect(() => {
    if (name) {
      setInfantDetailsFormValue('firstName', name, {
        shouldValidate: true,
      });
    }
  }, [name, setInfantDetailsFormValue]);

  // Set the gender button group to the correct value when genderId prop changes
  useEffect(() => {
    if (genderId) {
      setInfantDetailsFormValue('genderId', genderId, {
        shouldValidate: true,
      });
    }
  }, [genderId, setInfantDetailsFormValue]);

  // Initialize date state when dateOfBirth prop is provided
  useEffect(() => {
    if (dateOfBirth) {
      const birthDate = new Date(dateOfBirth);
      setMyDay(birthDate);
      setMyMonth(birthDate);
      setMyYear(birthDate);
      setInfantDetailsFormValue('dateOfBirth', birthDate);
    }
  }, [dateOfBirth, setInfantDetailsFormValue]);

  useEffect(() => {
    setMyDay(
      new Date(myYear.getFullYear(), myMonth.getMonth(), myDay.getDate())
    );
  }, [myMonth, myYear, setMyDay]);

  useEffect(() => {
    if (myDay) {
      setInfantDetailsFormValue('dateOfBirth', myDay);
    }
  }, [myDay, setInfantDetailsFormValue]);

  const infantVisits = getVisitDates('', myDay, visitTypes);

  const renderDayContents = (day: any, date: any) => {
    if (date < minDate || date > maxDate) {
      return <span></span>;
    }
    return <span>{date.getDate()}</span>;
  };

  const onChangeMonth = (date: Date) => {
    setMyDay((prevDate) => setMonth(prevDate, date.getMonth()));
    setMyYear((prevDate) => setMonth(prevDate, date.getMonth()));
    return setMyMonth(date);
  };

  const onChangeYear = (date: Date) => {
    if (expectedDateOfDelivery) {
      if (date.getFullYear() < expectedDateOfDelivery.getFullYear()) {
        setMyDay(eddMinDate);
        setMyMonth(eddMinDate);
        return setMyYear(eddMinDate);
      }

      if (date.getFullYear() > expectedDateOfDelivery.getFullYear()) {
        setMyDay(eddMaxDate);
        setMyMonth(eddMaxDate);
        return setMyYear(eddMaxDate);
      }
    }

    if (date.getFullYear() === currentDate.getFullYear()) {
      setMyDay(currentDate);
      setMyMonth(currentDate);
      return setMyYear(date);
    }

    if (date.getFullYear() === twoYearsAgo.getFullYear()) {
      setMyDay(twoYearsAgo);
      setMyMonth(twoYearsAgo);
      return setMyYear(twoYearsAgo);
    }

    setMyDay((prevDate) => setYear(prevDate, date.getFullYear()));
    setMyMonth((prevDate) => setYear(prevDate, date.getFullYear()));
    return setMyYear(date);
  };

  const handleDateChangeRaw = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  return (
    <>
      <Typography
        type="h2"
        color={'textDark'}
        text={
          numberOfChildren && numberOfChildren > 1
            ? `Child ${multipleChildrenCount}`
            : 'Child'
        }
        className="pt-6"
      />
      <Typography
        type="h4"
        color={'textMid'}
        text={'Details'}
        className="w-11/12 pt-2"
      />
      <div className="flex w-11/12 justify-center text-red-400">
        <Divider dividerType="dashed" />
      </div>
      <FormInput<InfantDetailsModel>
        label={'First name'}
        register={infantFormRegister}
        nameProp={'firstName'}
        placeholder={'First name'}
        type={'text'}
        className="mt-4"
        value={name}
      ></FormInput>
      <div className="mt-4">
        <Typography
          type="h4"
          color={'textMid'}
          text={'Date of birth:'}
          className="mt-4 w-11/12 pt-2"
        />
        <div className="flex items-center gap-1">
          <DatePicker
            placeholderText={'Please select a date'}
            className="text-textMid bg-uiBg focus:border-primary focus:ring-primary mt-1 w-full rounded-md border-none text-lg shadow-sm"
            selected={myDay}
            onChange={(date: Date) => setMyDay(date || currentDate)}
            dateFormat="dd"
            renderDayContents={renderDayContents}
            disabledKeyboardNavigation
            onFocus={(e) => e.target.blur()}
            onChangeRaw={(e) => handleDateChangeRaw(e)}
            renderCustomHeader={() => <></>}
            {...(myMonth.getMonth() === currentDate.getMonth() && {
              maxDate: currentDate,
            })}
            {...(!!expectedDateOfDelivery && {
              minDate: eddMinDate,
              maxDate: eddMaxDate,
            })}
            {...(!expectedDateOfDelivery && {
              minDate: twoYearsAgo,
            })}
          />
          <DatePicker
            placeholderText={'Please select a date'}
            className="text-textMid bg-uiBg focus:border-primary focus:ring-primary mt-1 w-full rounded-md border-none text-lg shadow-sm"
            selected={myMonth}
            onChange={(date: Date) => onChangeMonth(date || currentDate)}
            renderCustomHeader={() => <></>}
            dateFormat="MMMM"
            showMonthYearPicker
            disabledKeyboardNavigation
            onFocus={(e) => e.target.blur()}
            onChangeRaw={(e) => handleDateChangeRaw(e)}
            showPopperArrow={true}
            {...(!!expectedDateOfDelivery && {
              minDate: eddMinDate,
              maxDate: eddMaxDate,
            })}
            {...(!expectedDateOfDelivery &&
              myYear.getFullYear() === currentDate.getFullYear() && {
                maxDate: currentDate,
              })}
            {...(!expectedDateOfDelivery && {
              minDate: twoYearsAgo,
            })}
          />
          <DatePicker
            placeholderText={'Please select a date'}
            className="bg-uiBg text-textMid focus:border-primary focus:ring-primary mt-1 w-full rounded-md border-none text-lg shadow-sm"
            selected={myYear}
            onChange={(date: Date) => onChangeYear(date || currentDate)}
            dateFormat="yyyy"
            disabledKeyboardNavigation
            onFocus={(e) => e.target.blur()}
            onChangeRaw={(e) => handleDateChangeRaw(e)}
            showYearPicker
            {...(!!expectedDateOfDelivery
              ? {
                  minDate: eddMinDate,
                  maxDate: eddMaxDate,
                }
              : {
                  minDate: twoYearsAgo,
                  maxDate: currentDate,
                })}
          />
        </div>
        {years! < 1 && months! < 1 && (
          <div className="mt-6 flex w-full justify-start">
            <Alert
              type={'info'}
              message={`${days} days old`}
              className="w-full"
            />
          </div>
        )}
        {years! < 1 && months! > 0 && (
          <div className="mt-6 flex w-full justify-start">
            <Alert
              type={'info'}
              message={`${months} months and ${days} days old`}
              className="w-full"
            />
          </div>
        )}
        {years! > 0 && (
          <div className="mt-6 flex w-full justify-start">
            <Alert
              type={'info'}
              message={`${years} years and ${months} months old`}
              className="w-full"
            />
          </div>
        )}
      </div>
      <Typography
        type="h3"
        color={'textDark'}
        text={'Sex'}
        className="w-11/12 pt-2"
      />
      <div className="mt-2">
        <ButtonGroup<string>
          options={genderOptionsUpdated}
          selectedOptions={genderId}
          onOptionSelected={(value: string | string[]) => {
            setInfantDetailsFormValue('genderId', value as string, {
              shouldValidate: true,
            });
          }}
          color="secondary"
          type={ButtonGroupTypes.Button}
          className={'w-full'}
        />
      </div>
      <div className="flex h-full items-end">
        <Button
          type={'filled'}
          color={'primary'}
          className={'bottom-10 mt-2 max-h-10 w-full'}
          textColor={'white'}
          text={`Next`}
          icon={'ArrowCircleRightIcon'}
          iconPosition={'start'}
          onClick={() => {
            setInfantVisits(infantVisits);
            onSubmit(getInfantDetailsFormValues());
          }}
          disabled={!isValid}
        />
      </div>
    </>
  );
};
